import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { createPortal } from "react-dom";
import type { Anchor } from "../../state/ui";

/** Clicks inside elements marked with this attribute don't count as "outside" (nested menus). */
export const KEEP_OPEN = { "data-keep-open": "" };

function isInsideKeepOpen(target: EventTarget | null): boolean {
  return target instanceof Element && Boolean(target.closest("[data-keep-open]"));
}

export function useOutsideClose(ref: React.RefObject<HTMLElement | null>, onClose: () => void, enabled = true) {
  useEffect(() => {
    if (!enabled) return;
    const onDown = (e: PointerEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node) && !isInsideKeepOpen(e.target)) onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose();
      }
    };
    // Deferred so the click that opened it doesn't immediately close it.
    const t = setTimeout(() => document.addEventListener("pointerdown", onDown), 0);
    document.addEventListener("keydown", onKey);
    return () => {
      clearTimeout(t);
      document.removeEventListener("pointerdown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [ref, onClose, enabled]);
}

interface PopoverProps {
  anchor: Anchor;
  onClose: () => void;
  children: ReactNode;
  /** "side": beside the anchor (event popovers); "below": dropdown-like. */
  placement?: "side" | "below";
  width?: number;
  className?: string;
  style?: CSSProperties;
  keepOpen?: boolean;
}

/**
 * Floating card positioned next to an anchor rect :
 * right of it when there's room, else left, else centered; always clamped on screen.
 */
export function Popover({ anchor, onClose, children, placement = "side", width, className = "", style, keepOpen }: PopoverProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);
  useOutsideClose(ref, onClose);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const w = el.offsetWidth;
    const h = el.offsetHeight;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const gap = 8;
    let left: number;
    let top: number;
    if (placement === "below") {
      left = Math.min(Math.max(8, anchor.x), vw - w - 8);
      top = anchor.y + anchor.height + 4;
      if (top + h > vh - 8) top = Math.max(8, anchor.y - h - 4);
    } else {
      if (anchor.x + anchor.width + gap + w <= vw - 8) left = anchor.x + anchor.width + gap;
      else if (anchor.x - gap - w >= 8) left = anchor.x - gap - w;
      else left = (vw - w) / 2;
      top = Math.min(Math.max(8, anchor.y), vh - h - 8);
    }
    setPos({ left: Math.max(8, left), top: Math.max(8, top) });
  }, [anchor.x, anchor.y, anchor.width, anchor.height, placement]);

  // `autoFocus` fires while the card is still hidden (before it's positioned) and a
  // hidden element can't take focus — so focus [data-autofocus] once it's visible.
  const placed = pos !== null;
  useEffect(() => {
    if (placed) ref.current?.querySelector<HTMLElement>("[data-autofocus]")?.focus();
  }, [placed]);

  return createPortal(
    <div
      ref={ref}
      className={`popover ${className}`}
      role="dialog"
      {...(keepOpen ? KEEP_OPEN : {})}
      style={{ width, left: pos?.left ?? -9999, top: pos?.top ?? -9999, visibility: pos ? "visible" : "hidden", ...style }}
    >
      {children}
    </div>,
    document.body
  );
}
